import Button from '@dustin-web/microsite-components/src/button'
import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { StandardRegisterProps } from '.'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import {
  GetCompanyInfo,
  GetCompanyInfoQuery,
  GetCompanyInfoQueryVariables,
} from 'src/graphql/generated/graphql-types'
import Spinner from '@dustin-web/microsite-components/src/spinner'
import classNames from 'classnames'
import ValidationMessage from 'src/components/validationMessage'
import { GaRegEvent } from 'src/components/gaRegistrationHook/gaRegistrationEventHook'

enum State {
  editOrg,
  editCompanyName,
  displayCompanyName,
  runningQuery,
  error,
}

enum ShowOrganizationNumberErrorMessage {
  none,
  invalidOrganizationNumber,
  disallowNewContacts,
}

type Props = {
  onNavigateBack: () => void
}

const CompanyInfo = ({ onNavigateBack }: Props) => {
  const { site } = useSiteContext()

  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<StandardRegisterProps>()
  const { translate } = useTranslations()

  const [showOrganizationNumberErrorMessage, setShowOrganizationNumberErrorMessage] =
    useState<ShowOrganizationNumberErrorMessage>(ShowOrganizationNumberErrorMessage.none)

  const watchIsAuthenticated: boolean = watch('isAuthenticated')
  const watchCompanyName: string = watch('companyName')
  const watchSetByOrgNumber: boolean = watch('companyNameSetByOrgNumber')
  const watchOrgNumber: string = watch('organizationNumber')

  const { companyGaEvent } = GaRegEvent()

  const [state, setState] = useState<State>(
    !watchCompanyName
      ? State.editOrg
      : watchSetByOrgNumber
      ? State.displayCompanyName
      : State.editCompanyName
  )

  const validOrgNumber: boolean = useMemo(() => {
    if (/^(?=.*[0-9])[0-9 -.]+$/.test(watchOrgNumber)) {
      const digits: string = watchOrgNumber.replace(/\D/g, '')

      switch (site) {
        case 'DustinSweden':
          return digits.length === 10
        case 'DustinNorway':
          return digits.length === 9 && ['8', '9'].includes(digits[0])
        case 'DustinDenmark':
        case 'DustinFinland':
          return digits.length === 8
        case 'DustinNetherlands': {
          const regex = new RegExp('^[0-9]{8}$')
          return regex.test(watchOrgNumber)
        }
        case 'DustinBelgium': {
          // Ten straight digits 0123456789 or in sections 0123.456.789   
          const regex = new RegExp('^(?:\\d{10}|\\d{4}\\.\\d{3}\\.\\d{3})$')
          return regex.test(watchOrgNumber)
        }
      }
    }

    return false
  }, [watchOrgNumber, site])

  useEffect(() => {
    const orgnumber = ''
    if (orgnumber) {
      setState(State.runningQuery)
    }
  }, [])

  useGraphql<GetCompanyInfoQuery, GetCompanyInfoQueryVariables>(GetCompanyInfo, {
    context: {
      skip: state !== State.runningQuery,
    },
    variables: {
      orgNumber: watchOrgNumber,
    },
    swrOptions: {
      revalidateIfStale: true,
      onSuccess: data => {
        const companyName: string | null = data?.companyInfo?.companyName ?? null
        const disallowNewContacts: boolean = data?.companyInfo?.disallowNewContacts ?? true
        const isRegisteredCompany: boolean = data?.companyInfo?.isRegisteredCompany ?? true
        const isInvalidOrganizationNumber: boolean =
          data?.companyInfo?.isInvalidOrganizationNumber ?? true
        const unkonwError: boolean = !data

        if (unkonwError) {
          setState(State.error)
          return
        }

        if (isInvalidOrganizationNumber || !isRegisteredCompany) {
          setShowOrganizationNumberErrorMessage(
            ShowOrganizationNumberErrorMessage.invalidOrganizationNumber
          )
          setState(State.editOrg)
          return
        }
        
        if (disallowNewContacts) {
          setShowOrganizationNumberErrorMessage(
            ShowOrganizationNumberErrorMessage.disallowNewContacts
          )
          setState(State.editOrg)
          return
        }

        setShowOrganizationNumberErrorMessage(ShowOrganizationNumberErrorMessage.none)
        setValue('disallowNewContacts', disallowNewContacts)

        if (companyName) {
          setValue('companyName', companyName)
          setValue('companyNameSetByOrgNumber', true)
          setState(State.displayCompanyName)
        } else {
          setState(State.editCompanyName)
          setValue('companyNameSetByOrgNumber', false)
        }
      },
    },
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (
      showOrganizationNumberErrorMessage ===
      ShowOrganizationNumberErrorMessage.invalidOrganizationNumber
    ) {
      setShowOrganizationNumberErrorMessage(ShowOrganizationNumberErrorMessage.none)
    }

    setValue('organizationNumber', e.target.value)
  }

  const handleClickGet = () => {
    setState(State.runningQuery)
  }

  const handleClickUnlock = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (state === State.displayCompanyName) {
      setValue('companyName', '')
    }

    setState(State.editOrg)
  }

  return (
    <div>
      {state === State.runningQuery && <Spinner dustinSpinner />}

      <div className="row">
        <div className="col-12 col-lg-6">
          <div className="row">
            <div className="col">
              <label htmlFor="register-org-number">
                {translate('RegisterBusiness_Form_OrgNumber')}
              </label>
            </div>
          </div>
          <div className="row">
            <div
              className={classNames('col-7 c-form__item c-form__item--mandatory', {
                'c-form__error':
                  showOrganizationNumberErrorMessage ===
                  ShowOrganizationNumberErrorMessage.invalidOrganizationNumber,
              })}>
              <input
                disabled={[State.editCompanyName, State.displayCompanyName].includes(state)}
                className={classNames({
                  'form-item-disabled': [State.editCompanyName, State.displayCompanyName].includes(
                    state
                  ),
                })}
                id="register-org-number"
                autoComplete="off"
                type="text"
                {...register('organizationNumber')}
                onChange={handleChange}
                aria-invalid={errors.organizationNumber ? 'true' : 'false'}
                aria-describedby={errors.organizationNumber ? 'organisationNumberError' : undefined}
              />
              {(showOrganizationNumberErrorMessage ===
                ShowOrganizationNumberErrorMessage.invalidOrganizationNumber ||
                errors.organizationNumber?.message) && (
                <ValidationMessage id="organisationNumberError">
                  {translate('Validation_IncorrectOrgNumber')}
                </ValidationMessage>
              )}
              {showOrganizationNumberErrorMessage ===
                ShowOrganizationNumberErrorMessage.disallowNewContacts && (
                <ValidationMessage id="organisationNumberError">
                  {translate('Register_Error_DisallowNewContact')}
                </ValidationMessage>
              )}
            </div>
            <div className="col-5 u-pl-4">
              {state === State.editOrg ? (
                <Button
                  id="btn-org-lookup"
                  type="button"
                  primary
                  className="u-w-100 u-px-0"
                  style={{ minWidth: 'initial' }}
                  disabled={!validOrgNumber}
                  onClick={handleClickGet}
                  text={translate('RegisterBusiness_Form_OrgNumberLookup')}
                />
              ) : (
                <Button
                  type="button"
                  className="u-w-100"
                  onClick={handleClickUnlock}
                  text={translate('RegisterBusiness_Form_OrgNumberUnlock')}
                />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col">
              <label htmlFor="register-company-name">{translate('Register_CompanyName')}</label>
            </div>
          </div>
          <div className="row">
            <div
              className={classNames('col c-form__item c-form__item--mandatory', {
                'c-form__error': errors.companyName,
              })}>
              <input
                disabled={state !== State.editCompanyName}
                className={classNames({ 'form-item-disabled': state !== State.editCompanyName })}
                id="register-company-name"
                autoComplete="off"
                type="text"
                placeholder={translate('CompanyName_Placeholder')}
                {...register('companyName')}
                aria-invalid={errors.companyName ? 'true' : 'false'}
                aria-describedby={errors.companyName ? 'companyNameError' : undefined}
              />
              {errors?.companyName && (
                <ValidationMessage id="companyNameError">
                  {translate('Validation_CompanyNameIsRequired')}
                </ValidationMessage>
              )}
            </div>
          </div>
          <div className="row u-pt-12">
            <div
              className={classNames('col-12 col-lg-auto', { 'u-pl-lg-4': !watchIsAuthenticated })}>
              <Button
                id="btn-company-info-proceed"
                disabled={state !== State.displayCompanyName && state !== State.editCompanyName}
                className="u-w-100 u-w-lg-auto"
                icon="arrow-right"
                primary
                type="submit"
                text={translate('RegisterBuinsess_Procced')}
                onClick={() => companyGaEvent()}
              />
            </div>
            {!watchIsAuthenticated && (
              <div className="col-12 col-lg-auto u-pr-lg-4 order-lg-first">
                <Button
                  className="u-w-100 u-w-lg-auto"
                  text={translate('Registration_Back')}
                  onClick={onNavigateBack}
                />
              </div>
            )}
          </div>
          {state === State.error && (
            <div className="row">
              <div className="col-12">
                <ValidationMessage>{translate('REGISTRATION_UNKNOWN_ERROR')}</ValidationMessage>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CompanyInfo
